import { Box, Typography } from '@mui/material';
import { theme } from '@portals/core/src/themes/sde/main';
import { Bookmark, HeartOutlined } from '@portals/icons';
import { getFixedT } from 'i18next';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocalstorageState } from 'rooks';

import { BookmarkContext } from '../../context';
import { getSearchAgentsStorageKey } from '../../hooks/useSearchIsSaved/useSearchIsSaved';
import { EstateBookmarkedList } from '../EstateBookmakedList/EstateBookmarkedList';
import { TabItem, Tabpanel } from '../Tabpanel/Tabpanel';
import { SavedSearchContext } from './SavedSearches/SavedSearchContext/SavedSearchContext';
import { SavedSearches, SavedSearchParams } from './SavedSearches/SavedSearches';
import { getStyles } from './Watchlist.style';

const t = getFixedT(null, 'core-immobilien');

interface WatchlistProps {
  renderBookmarkButton?: (id: string, size?: 'small') => React.ReactElement;
  renderEstateLink?: (id: string, children: React.ReactNode) => React.ReactElement;
}

export const Watchlist: React.FC<WatchlistProps> = ({ renderBookmarkButton, renderEstateLink }: WatchlistProps) => {
  const { bookmarks } = useContext(BookmarkContext);
  const styles = getStyles(theme);

  const [estatesCount, setEstatesCount] = useState(0);
  const [savedSearches, setSavedSearches] = useLocalstorageState<SavedSearchParams[]>(getSearchAgentsStorageKey(), []);

  const [hasMountedInClient, setHasMountedInClient] = useState(false);

  useEffect(() => {
    setHasMountedInClient(true);
  }, []);

  useEffect(() => {
    setEstatesCount(bookmarks.length);
  }, [bookmarks]);

  const estatesDisplay = useMemo(() => {
    if (!hasMountedInClient) {
      return null;
    }
    return estatesCount < 1 ? null : estatesCount > 99 ? '99+' : estatesCount;
  }, [estatesCount, hasMountedInClient]);

  const searchesDisplay = useMemo(() => {
    if (!hasMountedInClient) {
      return null;
    }
    return savedSearches.length < 1 ? null : savedSearches.length > 99 ? '99+' : savedSearches.length;
  }, [savedSearches, hasMountedInClient]);

  const tabs: TabItem[] = [
    {
      label: (
        <>
          <Typography variant="h4" aria-label={`Tab title: ${t('watchlist.savedEstatesShort')}`} sx={styles.shortLabel}>
            {t('watchlist.savedEstatesShort')}
          </Typography>
          <Typography variant="h4" aria-label={`Tab title: ${t('watchlist.savedEstates')}`} sx={styles.longLabel}>
            {t('watchlist.savedEstates')}
          </Typography>
        </>
      ),
      counter: estatesDisplay,
      icon: <HeartOutlined />,
      content: <EstateBookmarkedList renderBookmarkButton={renderBookmarkButton} renderEstateLink={renderEstateLink} />,
    },
    {
      label: (
        <>
          <Typography
            variant="h4"
            aria-label={`Tab title: ${t('watchlist.savedSearchesShort')}`}
            sx={styles.shortLabel}
          >
            {t('watchlist.savedSearchesShort')}
          </Typography>
          <Typography variant="h4" aria-label={`Tab title: ${t('watchlist.savedSearches')}`} sx={styles.longLabel}>
            {t('watchlist.savedSearches')}
          </Typography>
        </>
      ),
      counter: searchesDisplay,
      icon: <Bookmark />,
      content: (
        <SavedSearchContext.Provider value={{ savedSearches, setSavedSearches }}>
          <SavedSearches />
        </SavedSearchContext.Provider>
      ),
    },
  ];

  return (
    <Box className="watchlist-container" sx={styles.outerContainer}>
      <Typography id="tabs" variant="h2" sx={styles.title}>
        {t('watchlist.title')}
      </Typography>
      <Tabpanel tabs={tabs} ariaLabelTitle={t('watchlist.title')} />
    </Box>
  );
};

Watchlist.displayName = 'Watchlist';
